import { useEffect, useRef, useState } from "react";
import { Spinner } from "../../../../../components/Spinner/Spinner.tsx";
import { useWorkspaceFonts } from "../../../../hooks/useWorkspaceFonts.ts";
import { useSelectedAsset } from "../../../hooks/useSelectedAsset.ts";
import type { DetailedAsset } from "../../../types.ts";
import { DesignerCanvas } from "../Designer/DesignerCanvas.tsx";
import { useSelectLayers } from "../Designer/hooks/useSelectLayers.ts";
import { designerCanvasStore } from "../Designer/stores/designerCanvasStore.ts";
import type { BaseCanvasStore } from "./FabricCanvas/canvasStoreFactory.ts";

export const EditContainer = ({
  areLayersLocked = true,
  overlayCanvasStore,
}: {
  areLayersLocked?: boolean;
  overlayCanvasStore?: BaseCanvasStore;
}) => {
  const { selectedAsset } = useSelectedAsset();

  const ref = useRef<HTMLDivElement>(null);
  const fabricCanvas = designerCanvasStore.useState((it) => it.fabricCanvas);
  const { deselectLayers } = useSelectLayers();

  const { workspaceFonts, builtinFonts } = useWorkspaceFonts();
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // We automatically deselect layers when clicking outside the canvas, but inside the edit container.
  // This is useful to avoid losing the selection when clicking in the settings menu...
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!fabricCanvas) return;

      const canvasElement = fabricCanvas.getElement();
      const canvasContainer = canvasElement.parentElement;
      const eventTarget = event.target as Node;

      if (
        !canvasElement.contains(eventTarget) &&
        !canvasContainer?.contains(eventTarget) &&
        ref.current?.contains(eventTarget) &&
        !areLayersLocked
      ) {
        deselectLayers();
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [fabricCanvas, areLayersLocked, deselectLayers]);

  // Fonts must be loaded before being used in the canvas
  // otherwise any text drawn will fallback onto some available
  // default font.
  useEffect(() => {
    const existingFontFamilies = Array.from(document.fonts.values()).map(
      (f) => f.family,
    );
    for (const font of [...workspaceFonts, ...builtinFonts]) {
      if (existingFontFamilies.includes(font.family)) continue;
      const fontFace = new FontFace(font.family, `url("${font.font_url}")`);
      document.fonts.add(fontFace);
      void fontFace.load();
    }
    // FIXME: Since we never set fontsLoaded back to false,
    // this means we do not support uploading new fonts from the Edit workspace,
    // as this will not trigger a re-render.
    void document.fonts.ready.then(() => setFontsLoaded(true));
  }, [workspaceFonts, builtinFonts, setFontsLoaded]);

  return (
    <div ref={ref} className="w-full h-full">
      {selectedAsset && fontsLoaded ? (
        <EditContainerContent
          asset={selectedAsset}
          areLayersLocked={areLayersLocked}
          overlayCanvasStore={overlayCanvasStore}
        />
      ) : (
        <Spinner />
      )}
    </div>
  );
};

const EditContainerContent = ({
  asset,
  areLayersLocked,
  overlayCanvasStore,
}: {
  asset: DetailedAsset;
  areLayersLocked?: boolean;
  overlayCanvasStore?: BaseCanvasStore;
}) => (
  <DesignerCanvas
    asset={asset}
    areLayersLocked={areLayersLocked}
    overlayCanvasStore={overlayCanvasStore}
  />
);
